/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {ObserveType, REQUEST_OPTIONS, ResponseType} from '../../../app.constants';
import {ReportDefinition, ReportsFormData} from '../../../models/models.interfaces';
import {ApiService} from '../../api';
import {FormGeneratorService} from '../../form-generator/form-generator.service';
import {FileModel, mapToFileModel} from '../../utils/files';
import {ProfileType} from '../contract/contract.interfaces';


@Injectable()
export class ReportDefinitionService {

  constructor(private api: ApiService,
              private formGeneratorService: FormGeneratorService) {
  }

  public getReportDefinitionByType(cliRegIds: Array<number>, types: Array<string>, mySpaceCliRegId?: number, profileType?: ProfileType)
    : Observable<ReportsFormData> {
    const cliRegsParameters = cliRegIds.map((cliRegId: number) => `cliRegs=${cliRegId}`).join('&');
    const typeParameters = types.map((type: string) => `type=${type}`).join('&');
    let requestObservable: Observable<any>;
    if (mySpaceCliRegId) {
      requestObservable = this.api.config.get(`/mySpace/${mySpaceCliRegId}/report?${cliRegsParameters}&${typeParameters}&profile-type=${profileType}`);
    } else {
      requestObservable = this.api.config.get(`/regulation/report?${cliRegsParameters}&${typeParameters}`);
    }
    return requestObservable
      .pipe(
        map((reportDefinitions) => this.formGeneratorService.transformReportDefinitions(reportDefinitions))
      );
  }

  public getReportDefinitionsByCliRegIdAndType(cliRegIds: number[], types: string[]): Observable<ReportDefinition[]> {
    if (!cliRegIds || cliRegIds.length <= 0) {
      return of([]);
    }
    const cliRegsParameters = cliRegIds.map((cliRegId: number) => `cli-reg-ids=${cliRegId}`).join('&');
    const typesParameters = types.map((type: string) => `types=${type}`).join('&');
    const requestObservable = this.api.get(`/repository/api/v1/regulation/reports?${cliRegsParameters}&${typesParameters}`);
    return requestObservable
      .pipe(
        map((reportDefinitions: ReportDefinition[]) => {
          reportDefinitions.forEach(report => report.id = report.reportName + report.gloRegId);
          return reportDefinitions;
        })
      );
  }

  public prepareReport(cliReg: number, reportName: string, data): Observable<any> {
    return this.api.report.post(`/reportMotor/cliReg/${cliReg}/${reportName}`, data, REQUEST_OPTIONS.RESPONSE_TYPE.BLOB);
  }

  public hasDuplicate(report: ReportDefinition, reports: ReportDefinition[]): boolean {
    if (reports && reports.length > 0) {
      return reports
        .filter(collectiveReport => collectiveReport.reportName === report.reportName).length > 1;
    }
    return false;
  }

  public getReport(cliReg: number,
                   reportName: string,
                   body, silent?: boolean): Observable<FileModel | null> {
    let service = this.api.report;
    if (silent) {
      service = this.api.silent.report;
    }
    return service.post(`/reportMotor/cliReg/${cliReg}/${reportName}?immediate=true`, body,
      {
        requestOptions: {
          observe: ObserveType.RESPONSE,
          responseType: ResponseType.BLOB
        }
      })
      .pipe(
        map(mapToFileModel()),
        catchError(_ => of(null))
      );
  }
}
